/**
 * Admin debug actions component.
 *
 * Displays debug status and action buttons for testing observability.
 * Actions handled by runtime via callbacks.
 */

import * as stylex from "@stylexjs/stylex";

import { button, spacing, text } from "@animus/primaris";

import { styles } from "./styles.stylex.ts";

interface DebugStatus {
  status: string;
  user: {
    id: string;
    role: string;
  };
  timestamp: string;
}

interface HealthStatus {
  status: "ok" | "degraded";
  timestamp: string;
  version: string;
  environment: string;
  db: { status: "ok" | "error"; latencyMs?: number };
}

interface DebugAction {
  key: string;
  title: string;
  description: string;
  buttonLabel: string;
  pendingLabel: string;
  variant: "danger" | "warning" | "primary";
  isPending: boolean;
  lastTriggered: Date | null;
  onTrigger: () => Promise<void>;
}

interface AdminDebugProps {
  status: DebugStatus | null;
  isLoadingStatus?: boolean;
  actions: DebugAction[];
  health?: HealthStatus | null;
  isLoadingHealth?: boolean;
  healthError?: string | null;
}

function getButtonVariantStyle(variant: DebugAction["variant"]) {
  if (variant === "danger") {
    return button.danger;
  }
  if (variant === "warning") {
    return button.warning;
  }
  return button.primary;
}

function AdminDebug({
  status,
  isLoadingStatus,
  actions,
  health,
  isLoadingHealth,
  healthError,
}: AdminDebugProps) {
  return (
    <div>
      <div {...stylex.props(spacing.mb6)}>
        <h1 {...stylex.props(text.bold, text["2xl"], text.white)}>
          Debug Actions
        </h1>
        <p {...stylex.props(spacing.mt1, text.sm, text.muted)}>
          Test observability integrations (Sentry, OpenTelemetry)
        </p>
      </div>

      {/* API Health Check */}
      <div {...stylex.props(styles.statusCard)}>
        <h2 {...stylex.props(spacing.mb4, text.medium, text.white)}>
          API Health Check
        </h2>
        {isLoadingHealth && (
          <p {...stylex.props(text.sm, text.muted)}>Loading...</p>
        )}
        {healthError && (
          <div {...stylex.props(styles.space, text.sm)}>
            <p {...stylex.props(text.error)}>Error: {healthError}</p>
            <p {...stylex.props(text.muted)}>
              Make sure the API is running: bunx nx run @animus/api:dev
            </p>
          </div>
        )}
        {!(isLoadingHealth || healthError) && health && (
          <pre {...stylex.props(text.sm, text.success, styles.healthPre)}>
            {JSON.stringify(health, null, 2)}
          </pre>
        )}
      </div>

      {/* Status Card */}
      <div {...stylex.props(styles.statusCard)}>
        <h2 {...stylex.props(spacing.mb4, text.medium, text.white)}>
          Debug Status
        </h2>
        {isLoadingStatus && (
          <p {...stylex.props(text.sm, text.muted)}>Loading...</p>
        )}
        {!isLoadingStatus && status && (
          <div {...stylex.props(styles.space, text.sm)}>
            <p {...stylex.props(text.muted)}>
              Status:{" "}
              <span {...stylex.props(text.success)}>{status.status}</span>
            </p>
            <p {...stylex.props(text.muted)}>
              User:{" "}
              <span {...stylex.props(text.muted)}>
                {status.user.id} ({status.user.role})
              </span>
            </p>
            <p {...stylex.props(text.muted)}>
              Timestamp:{" "}
              <span {...stylex.props(text.muted)}>
                {new Date(status.timestamp).toLocaleString()}
              </span>
            </p>
          </div>
        )}
        {!(isLoadingStatus || status) && (
          <p {...stylex.props(text.sm, text.error)}>Failed to load status</p>
        )}
      </div>

      {/* Actions */}
      <div {...stylex.props(styles.actionCard)}>
        <h2 {...stylex.props(spacing.mb4, text.medium, text.white)}>Actions</h2>

        <div {...stylex.props(styles.space)}>
          {actions.map((action) => (
            <div {...stylex.props(styles.actionItem)} key={action.key}>
              <div>
                <h3 {...stylex.props(text.medium, text.white)}>
                  {action.title}
                </h3>
                <p {...stylex.props(spacing.mt1, text.sm, text.muted)}>
                  {action.description}
                </p>
                {action.lastTriggered && (
                  <p {...stylex.props(spacing.mt2, text.xs, text.success)}>
                    Last triggered: {action.lastTriggered.toLocaleTimeString()}
                  </p>
                )}
              </div>
              <button
                {...stylex.props(
                  button.base,
                  getButtonVariantStyle(action.variant),
                  button.small
                )}
                disabled={action.isPending}
                onClick={action.onTrigger}
                type="button"
              >
                {action.isPending ? action.pendingLabel : action.buttonLabel}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export {
  AdminDebug,
  type AdminDebugProps,
  type DebugAction,
  type DebugStatus,
  type HealthStatus,
};
